import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Users, Pencil, Check, Link2, Store, MapPinPlus, Undo2, LayoutGrid, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ACTIVE, toMin, eur, bookingTables, cassaCheck } from "../../lib/salaApi";
import { TableEditDialog } from "./TableEditDialog";

const COLS = 11;
const LONG_PRESS_MS = 220;

const overlap = (a, b, tol = 0.1) => a.x < b.x + b.w - tol && a.x + a.w > b.x + tol && a.y < b.y + b.h - tol && a.y + a.h > b.y + tol;
// Snap the dragged table flush to the nearest side of the target so merged tables look joined.
const snapNext = (t, target, maxX, maxY) => {
  const cx = t.x + t.w / 2 - (target.x + target.w / 2), cy = t.y + t.h / 2 - (target.y + target.h / 2);
  const horiz = Math.abs(cx) / (target.w + t.w) >= Math.abs(cy) / (target.h + t.h);
  const x = horiz ? (cx >= 0 ? target.x + target.w : target.x - t.w) : target.x + (target.w - t.w) / 2;
  const y = horiz ? target.y + (target.h - t.h) / 2 : (cy >= 0 ? target.y + target.h : target.y - t.h);
  return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) };
};
// Snap a dropped table onto the columns/rows of its neighbours of similar size, so the plan stays tidy.
const SNAP = 0.45;
const snapAligned = (t, others) => {
  let { x, y } = t;
  const near = (axis, size) => others.filter((o) => Math.abs((o[size] || 1.5) - (t[size] || 1.5)) < 0.35 && Math.abs(o[axis] - t[axis]) <= SNAP).sort((a, b) => Math.abs(a[axis] - t[axis]) - Math.abs(b[axis] - t[axis]))[0];
  const nx = near("x", "w"), ny = near("y", "h");
  if (nx) x = nx.x;
  if (ny) y = ny.y;
  return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
};
const autoPlace = (tables) => {
  const byArea = {};
  tables.forEach((t) => { (byArea[t.area || "Sala"] ||= []).push(t); });
  const out = [];
  Object.values(byArea).forEach((list) => {
    const placed = list.filter((t) => Number.isFinite(t.x) && Number.isFinite(t.y));
    const base = Math.max(0, ...placed.map((t) => t.y + (t.h || 1.4))) + 0.5;
    let i = 0;
    list.forEach((t) => {
      if (Number.isFinite(t.x) && Number.isFinite(t.y)) out.push({ w: 1.6, h: 1.4, shape: "rect", ...t });
      else { out.push({ w: 1.6, h: 1.4, shape: "rect", ...t, x: (i % 5) * 2.2, y: base + Math.floor(i / 5) * 1.9 }); i += 1; }
    });
  });
  return out;
};

export function FloorPlan({ tables, areas, rows, groups = [], mealMin, assigning, onAssign, onPickTable, onWalkIn, onMerge, onMove, onMoveDefault, onAlign, onResetDefault, onSaveLayout, selectedTable, pos = {}, service = "all", dayMoved = false, onResetPositions }) {
  const allTables = useMemo(() => autoPlace(tables), [tables]);
  const areaList = areas?.length ? areas : [...new Set(allTables.map((t) => t.area || "Sala"))];
  const [area, setArea] = useState(areaList[0]);
  const [edit, setEdit] = useState(false);
  const [editing, setEditing] = useState(null); // table being edited | "new"
  const [drag, setDrag] = useState(null); // { name, x, y }
  const boxRef = useRef(null);
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  useEffect(() => { if (!areaList.includes(area)) setArea(areaList[0]); }, [areaList, area]);

  const active = rows.filter((b) => ACTIVE.includes(b.status) && bookingTables(b).length);
  const byTable = useMemo(() => {
    const m = {};
    active.forEach((b) => bookingTables(b).forEach((t) => { (m[t] ||= []).push(b); }));
    Object.values(m).forEach((l) => l.sort((a, b) => toMin(a.time) - toMin(b.time)));
    return m;
  }, [active]);
  const list = allTables.filter((t) => (t.area || "Sala") === area).map((t) => (drag?.name === t.name ? { ...t, x: drag.x, y: drag.y } : t));
  const rowsH = Math.max(3, ...list.map((t) => t.y + t.h)) + 0.3;
  const areaCovers = (a) => active.filter((b) => allTables.find((t) => t.name === b.table)?.area === a && b.status === "seated").reduce((n, b) => n + b.party_size, 0);
  const groupOf = (name) => groups.find((g) => g.tables.includes(name));
  const areaGroups = groups.filter((g) => g.tables.some((n) => list.find((t) => t.name === n)));

  // ---- drag (long press) → move, and drop onto another table → merge
  const toUnits = (dxPx, dyPx) => { const r = boxRef.current.getBoundingClientRect(); return { dx: (dxPx / r.width) * COLS, dy: (dyPx / r.height) * rowsH }; };
  const dragRef = useRef(null);
  const setDragPos = (d) => { dragRef.current = d; setDrag(d); };
  const down = (t) => (e) => {
    if (assigning) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const g = { name: t.name, sx: e.clientX, sy: e.clientY, ox: t.x, oy: t.y, w: t.w, h: t.h, live: false, timer: 0 };
    g.timer = window.setTimeout(() => { g.live = true; setDragPos({ name: t.name, x: t.x, y: t.y }); navigator.vibrate?.(15); }, LONG_PRESS_MS);
    gesture.current = g;
  };
  const move = (e) => {
    const g = gesture.current;
    if (!g) return;
    const { dx, dy } = toUnits(e.clientX - g.sx, e.clientY - g.sy);
    if (!g.live) { if (Math.hypot(e.clientX - g.sx, e.clientY - g.sy) > 10) { clearTimeout(g.timer); gesture.current = null; } return; }
    setDragPos({ name: g.name, x: Math.min(Math.max(0, g.ox + dx), COLS - g.w), y: Math.min(Math.max(0, g.oy + dy), rowsH - g.h) });
  };
  const up = () => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    clearTimeout(g.timer);
    if (!g.live) return;
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 50);
    const d = dragRef.current;
    setDragPos(null);
    if (!d) return;
    const me = { ...g, x: d.x, y: d.y };
    const target = !edit && list.find((t) => t.name !== g.name && overlap(me, t));
    const emit = edit ? onMoveDefault || onMove : onMove;
    if (target) {
      emit?.([{ name: g.name, ...snapNext(me, target, COLS - g.w, rowsH - g.h) }]);
      onMerge?.([g.name, target.name]);
    } else if (Math.abs(d.x - g.ox) > 0.05 || Math.abs(d.y - g.oy) > 0.05) {
      const snapped = snapAligned(me, list.filter((t) => t.name !== g.name));
      const clash = list.some((t) => t.name !== g.name && overlap({ ...me, ...snapped }, t, 0.3));
      emit?.([{ name: g.name, ...(clash ? { x: d.x, y: d.y } : snapped) }]);
    }
  };

  const click = (t, use) => {
    if (suppressClick.current) return;
    if (edit) return setEditing(t);
    if (assigning) return onAssign(assigning, t.name);
    onPickTable(t, use, groupOf(t.name));
  };
  const saveTable = (next, original) => {
    const others = allTables.filter((t) => t.name !== original?.name);
    if (others.some((t) => t.name === next.name)) return toast.error("Esiste già un tavolo con questo nome");
    const nextAreas = areaList.includes(next.area) ? areaList : [...areaList, next.area];
    onSaveLayout?.([...others, next], nextAreas); setEditing(null);
  };
  const deleteTable = (t) => { onSaveLayout?.(allTables.filter((x) => x.name !== t.name), areaList); setEditing(null); };
  const addArea = () => { const n = window.prompt("Nome della nuova area (es. Giardino)"); if (n?.trim()) { onSaveLayout?.(allTables, [...areaList, n.trim()]); setArea(n.trim()); } };
  const checkCassa = async () => {
    try {
      const r = await cassaCheck();
      if (!r.configured) return toast.message("Cassa in Cloud non configurata");
      if (!r.missing_in_cassa.length && !r.missing_in_map.length) return toast.success("Tutti i tavoli della mappa esistono in cassa");
      if (r.missing_in_cassa.length) toast.error(`Non esistono in cassa: ${r.missing_in_cassa.join(", ")}`, { duration: 8000 });
      if (r.missing_in_map.length) toast.message(`In cassa ma non in mappa: ${r.missing_in_map.join(", ")}`, { duration: 8000 });
    } catch { toast.error("Verifica non riuscita"); }
  };

  return (
    <div className={`rounded-3xl border bg-white p-3 space-y-2.5 ${edit ? "border-[#C48B31] ring-2 ring-[#C48B31]/30" : "border-[#e8dcc8]"}`} data-testid="sala-floor-plan" data-edit={edit}>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 items-center">
        {areaList.map((a) => {
          const c = areaCovers(a);
          return (
            <button key={a} onClick={() => setArea(a)} data-testid={`sala-area-${a.replace(/\s+/g, "-").toLowerCase()}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 ${area === a ? "bg-[#4a1c17] text-white" : "bg-[#faf3ee] text-[#2b1410] border border-[#e8dcc8]"}`}>
              {a}{c > 0 && <span className={`rounded-full px-1.5 text-[10px] ${area === a ? "bg-white/20" : "bg-[#4a1c17] text-white"}`}>{c}</span>}
            </button>
          );
        })}
        <div className="flex-1" />
        {edit ? (
          <>
            <button onClick={() => setEditing("new")} className="shrink-0 rounded-full bg-white border border-[#C48B31] text-[#4a1c17] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="sala-edit-add-table"><Plus className="h-3.5 w-3.5" /> Tavolo</button>
            <button onClick={addArea} className="shrink-0 rounded-full bg-white border border-[#C48B31] text-[#4a1c17] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="sala-edit-add-area"><MapPinPlus className="h-3.5 w-3.5" /> Area</button>
            <button onClick={checkCassa} className="shrink-0 rounded-full bg-white border border-[#C48B31] text-[#4a1c17] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="sala-edit-cassa-check"><Store className="h-3.5 w-3.5" /> Verifica cassa</button>
            <button onClick={onAlign} title="Allinea i tavoli su righe e colonne pulite, mantenendo la disposizione attuale" className="shrink-0 rounded-full bg-white border border-[#C48B31] text-[#4a1c17] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="sala-edit-align"><LayoutGrid className="h-3.5 w-3.5" /> Allinea</button>
            <button onClick={onResetDefault} title="Riporta tutti i tavoli alla piantina originale" className="shrink-0 rounded-full bg-white border border-[#C48B31] text-[#4a1c17] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="sala-edit-reset-default"><RotateCcw className="h-3.5 w-3.5" /> Piantina originale</button>
            <button onClick={() => setEdit(false)} className="shrink-0 rounded-full bg-[#C48B31] text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="sala-edit-done"><Check className="h-3.5 w-3.5" /> Fine</button>
          </>
        ) : (
          <button onClick={() => setEdit(true)} title="Modifica mappa: sposta, aggiungi o cambia i tavoli" className="shrink-0 h-8 w-8 rounded-full bg-[#faf3ee] border border-[#e8dcc8] text-[#4a1c17] flex items-center justify-center" data-testid="sala-edit-toggle"><Pencil className="h-3.5 w-3.5" /></button>
        )}
      </div>
      {assigning && <div className="rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-900" data-testid="sala-assigning-hint"><b>{assigning.name}</b> · {assigning.party_size}p → tocca il tavolo (o la tavolata) da assegnare</div>}
      {edit && <div className="rounded-xl bg-[#fdf6e9] border border-[#C48B31]/40 px-3 py-2 text-xs text-[#4a1c17]" data-testid="sala-edit-hint"><b>Modifica mappa</b> · qui gli spostamenti diventano la disposizione fissa per tutti i giorni (fuori da questa modalità valgono solo per il giorno) · tieni premuto e trascina per spostare · tocca un tavolo per cambiare posti, nome, forma o eliminarlo</div>}
      <div ref={boxRef} className="relative mx-auto rounded-2xl bg-[#faf3ee] border border-[#efe3d5] select-none" style={{ aspectRatio: `${COLS} / ${rowsH}`, width: `min(100%, calc((100vh - 270px) * ${(COLS / rowsH).toFixed(4)}))` }}
        onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}>
        <div className="absolute inset-0 p-2">
          {areaGroups.map((g) => <GroupOutline key={g.id} g={g} list={list} rowsH={rowsH} booking={g.booking_id ? rows.find((b) => b.id === g.booking_id) : null} />)}
          {list.map((t) => {
            const use = byTable[t.name] || [];
            const seated = use.find((b) => b.status === "seated");
            const upcoming = use.find((b) => b.status === "confirmed");
            const conflict = use.some((a) => use.some((b) => a.id !== b.id && Math.abs(toMin(a.time) - toMin(b.time)) < mealMin));
            const grouped = groupOf(t.name);
            const tooSmall = assigning && !grouped && Number(t.seats) < Number(assigning.party_size);
            const p = pos[t.name] && (service === "all" || pos[t.name].service === service) ? pos[t.name] : null;
            const paying = seated && p?.state === "paying";
            const paid = !seated && !upcoming && p?.state === "paid";
            const state = paying ? "paying" : seated ? "seated" : upcoming ? "upcoming" : paid ? "paid" : "free";
            const cls = paying ? "bg-violet-600 text-white border-violet-700" : seated ? "bg-[#4a1c17] text-white border-[#4a1c17]" : upcoming ? "bg-amber-50 text-[#2b1410] border-amber-400" : grouped ? "bg-[#fdf6e9] text-[#2b1410] border-[#C48B31]" : "bg-white text-[#2b1410] border-emerald-300";
            const isDragging = drag?.name === t.name;
            return (
              <button key={t.name} data-testid={`sala-table-${t.name}`} data-state={state} data-grouped={!!grouped}
                onClick={() => click(t, use)} onPointerDown={down(t)} onContextMenu={(e) => e.preventDefault()}
                style={{ left: `${(t.x / COLS) * 100}%`, top: `${(t.y / rowsH) * 100}%`, width: `${(t.w / COLS) * 100}%`, height: `${(t.h / rowsH) * 100}%`, touchAction: "none" }}
                className={`absolute border-2 shadow-sm flex flex-col items-center justify-center text-center leading-tight ${isDragging ? "z-30 scale-105 shadow-2xl ring-4 ring-[#C48B31]/50 transition-none" : "transition-[transform,box-shadow,left,top] active:scale-95"} ${t.shape === "round" ? "rounded-full" : "rounded-xl"} ${cls} ${selectedTable === t.name ? "ring-4 ring-[#C48B31]/60" : ""} ${conflict ? "ring-4 ring-rose-400" : ""} ${tooSmall ? "opacity-40" : ""}`}>
                <span className={`font-bold ${t.shape === "round" ? "text-[10px] sm:text-xs" : "text-xs sm:text-sm"}`}>{t.name}</span>
                {(seated || upcoming)?.pos?.order?.id && !paying && <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-sky-400 ring-2 ring-white" title="Aperto in cassa" data-testid={`sala-table-pos-${t.name}`} />}
                {grouped && <Link2 className="absolute top-0.5 left-0.5 h-3 w-3 text-[#C48B31]" />}
                {t.shape !== "round" && (
                  <span className="text-[9px] sm:text-[10px] opacity-80 inline-flex items-center gap-0.5 truncate max-w-full px-1">
                    {paying ? `conto · ${eur(p.bill_amount)}` : seated ? (grouped ? `${seated.party_size}p · tavolata` : `${seated.name.split(" ")[0]} · ${seated.party_size}p`) : upcoming ? `${upcoming.time} · ${upcoming.party_size}p` : paid ? <span className="text-emerald-700 font-semibold" data-testid={`sala-table-paid-${t.name}`}>{eur(p.amount)} · {p.covers}c</span> : <><Users className="h-2.5 w-2.5" /> {t.seats}</>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#7c5a4e]">
        <i className="inline-block h-3 w-3 rounded-sm bg-white border-2 border-emerald-300" /> libero
        <i className="inline-block h-3 w-3 rounded-sm bg-amber-50 border-2 border-amber-400" /> prenotato
        <i className="inline-block h-3 w-3 rounded-sm bg-[#4a1c17]" /> al tavolo
        <i className="inline-block h-3 w-3 rounded-sm bg-violet-600" /> in pagamento
        <i className="inline-block h-3 w-3 rounded-sm bg-[#fdf6e9] border-2 border-[#C48B31]" /> tavolata
        <i className="inline-block h-2.5 w-2.5 rounded-full bg-sky-400" /> aperto in cassa
        <span className="hidden sm:inline">· tieni premuto e trascina un tavolo su un altro per unirli</span>
        {dayMoved && <button onClick={onResetPositions} className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] bg-white text-[#4a1c17] px-3 py-1.5 text-xs font-bold" title="Torna alla disposizione di default (solo per oggi sono stati spostati dei tavoli)" data-testid="sala-reset-positions-btn"><Undo2 className="h-3.5 w-3.5" /> Disposizione originale</button>}
        <button onClick={() => onWalkIn(null)} className={`${dayMoved ? "" : "ml-auto "}inline-flex items-center gap-1 rounded-full bg-[#C48B31] text-white px-3 py-1.5 text-xs font-bold`} data-testid="sala-walkin-plan-btn"><Plus className="h-3.5 w-3.5" /> Walk-in</button>
      </div>
      {editing && <TableEditDialog table={editing === "new" ? null : editing} area={area} areas={areaList} onClose={() => setEditing(null)} onSave={saveTable} onDelete={deleteTable} />}
    </div>
  );
}

function GroupOutline({ g, list, rowsH, booking }) {
  const ts = g.tables.map((n) => list.find((t) => t.name === n)).filter(Boolean);
  if (!ts.length) return null;
  const pad = 0.18;
  const x0 = Math.min(...ts.map((t) => t.x)) - pad, y0 = Math.min(...ts.map((t) => t.y)) - pad;
  const x1 = Math.max(...ts.map((t) => t.x + t.w)) + pad, y1 = Math.max(...ts.map((t) => t.y + t.h)) + pad;
  const seats = ts.reduce((n, t) => n + Number(t.seats || 0), 0);
  const label = booking ? `${booking.name} · ${booking.party_size}p` : `Tavolata · ${seats} posti · tocca per sedere`;
  return (
    <div className="absolute rounded-2xl border-[3px] border-dashed border-[#C48B31] bg-[#C48B31]/10 pointer-events-none" data-testid={`sala-group-${g.id}`}
      style={{ left: `${(x0 / COLS) * 100}%`, top: `${(y0 / rowsH) * 100}%`, width: `${((x1 - x0) / COLS) * 100}%`, height: `${((y1 - y0) / rowsH) * 100}%` }}>
      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#C48B31] text-white px-2 py-0.5 text-[10px] font-bold shadow">{label}</span>
    </div>
  );
}
